import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import authService from '../services/authService';
import Navbar from './Navbar';
import Footer from './Footer';

const Discussions = () => {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [userVotes, setUserVotes] = useState({}); // Store user votes by discussion ID
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clubId = searchParams.get('clubId');
  const boardGameId = searchParams.get('boardGameId');
  const isGeneral = searchParams.get('isGeneral');

  useEffect(() => {
    fetchDiscussions();
  }, [page, clubId, boardGameId, isGeneral]);

  const fetchDiscussions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(clubId && { clubId }),
        ...(boardGameId && { boardGameId }),
        ...(isGeneral && { isGeneral })
      });

      const response = await fetch(`/api/discussions?${params}`);
      const data = await response.json();

      if (data.success) {
        setDiscussions(data.data);
        setPagination(data.pagination);
        
        // Fetch user votes for all discussions if authenticated
        if (authService.isAuthenticated()) {
          fetchUserVotes(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching discussions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVotes = async (discussionsList) => {
    try {
      const votes = {};
      await Promise.all(
        discussionsList.map(async (discussion) => {
          try {
            const response = await authService.get(`/api/discussions/${discussion.slug}/vote`);
            if (response.success) {
              votes[discussion.id] = response.data.userVote;
            }
          } catch (err) {
            // Ignore individual vote fetch errors
          }
        })
      );
      setUserVotes(votes);
    } catch (err) {
      console.error('Error fetching user votes:', err);
    }
  };

  const handleVote = async (discussionSlug, discussionId, voteType) => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const response = await authService.post(`/api/discussions/${discussionSlug}/vote`, {
        voteType
      });

      if (response.success) {
        // Update discussion vote counts
        setDiscussions(discussions.map(d => 
          d.slug === discussionSlug 
            ? { ...d, upvotes: response.data.upvotes, downvotes: response.data.downvotes }
            : d
        ));
        
        // Update user vote
        setUserVotes({ ...userVotes, [discussionId]: response.data.userVote });
      }
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const handleNewDiscussion = () => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    navigate('/discussions/new');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-base-200">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div>
            <h1 className="text-5xl font-bold mb-2">Discussions</h1>
            <p className="text-xl text-base-content/70">
              Join the conversation about board games
            </p>
          </div>
          <button onClick={handleNewDiscussion} className="btn btn-primary">
            New Discussion
          </button>
        </motion.div>

        {/* Filter Badges */}
        {(clubId || boardGameId || isGeneral) && (
          <div className="flex gap-2 mb-6">
            {clubId && <div className="badge badge-lg">Filtered by Club</div>}
            {boardGameId && <div className="badge badge-lg">Filtered by Board Game</div>}
            {isGeneral && <div className="badge badge-lg">General Discussions</div>}
            <Link to="/discussions" className="badge badge-ghost badge-lg">
              Clear Filters
            </Link>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        ) : (
          <>
            {/* Discussions List - Reddit Style */}
            <div className="space-y-2 mb-8">
              {discussions.map((discussion, index) => (
                <motion.div
                  key={discussion.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-base-100 rounded-md border border-base-300 hover:border-base-content/20 transition-all"
                >
                  <div className="flex">
                    {/* Voting Section - Left Side */}
                    <div className="flex flex-col items-center bg-base-200 px-3 py-4 rounded-l-md">
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          handleVote(discussion.slug, discussion.id, 'up');
                        }}
                        className={`btn btn-ghost btn-xs p-0 min-h-0 h-6 ${
                          userVotes[discussion.id] === 'up' 
                            ? 'text-success' 
                            : 'hover:text-success'
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill={userVotes[discussion.id] === 'up' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <span className="text-sm font-bold my-1">
                        {(discussion.upvotes || 0) - (discussion.downvotes || 0)}
                      </span>
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          handleVote(discussion.slug, discussion.id, 'down');
                        }}
                        className={`btn btn-ghost btn-xs p-0 min-h-0 h-6 ${
                          userVotes[discussion.id] === 'down' 
                            ? 'text-error' 
                            : 'hover:text-error'
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill={userVotes[discussion.id] === 'down' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Main Content */}
                    <Link to={`/discussions/${discussion.slug}`} className="flex-1 p-3 hover:bg-base-200/50 transition-colors">
                      <div className="flex items-start gap-2 mb-1">
                        {/* Tags/Communities - Small */}
                        <div className="flex flex-wrap gap-1.5 text-xs">
                          {discussion.boardGameLinks?.map((link) => (
                            <span key={link.boardGame.id} className="badge badge-primary badge-sm gap-1">
                              🎲 {link.boardGame.name}
                            </span>
                          ))}
                          {discussion.clubLinks?.map((link) => (
                            <span key={link.club.id} className="badge badge-secondary badge-sm gap-1">
                              🏛️ {link.club.name}
                            </span>
                          ))}
                          {discussion.isGeneral && (
                            <span className="badge badge-accent badge-sm">General</span>
                          )}
                        </div>
                        
                        {/* Author & Time */}
                        <div className="text-xs text-base-content/60 flex items-center gap-1.5 ml-auto shrink-0">
                          <span>Posted by u/{discussion.author.firstName}{discussion.author.lastName}</span>
                          <span>•</span>
                          <span>{formatDate(discussion.createdAt)}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2 hover:text-primary transition-colors">
                        {discussion.isPinned && <span className="text-success text-sm">📌</span>}
                        {discussion.title}
                        {discussion.isLocked && <span className="text-error text-sm">🔒</span>}
                      </h2>

                      {/* Content Preview */}
                      <p className="text-sm text-base-content/70 line-clamp-2 mb-2">
                        {discussion.content}
                      </p>

                      {/* Stats Bar */}
                      <div className="flex gap-4 text-xs text-base-content/60 font-medium">
                        <span className="flex items-center gap-1">
                          💬 {discussion._count?.replies || discussion.replyCount} comments
                        </span>
                        <span className="flex items-center gap-1">
                          👁️ {discussion.viewCount} views
                        </span>
                        <button className="flex items-center gap-1 hover:text-base-content">
                          Share
                        </button>
                        <button className="flex items-center gap-1 hover:text-base-content">
                          Save
                        </button>
                      </div>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Empty State */}
            {discussions.length === 0 && (
              <div className="text-center py-20">
                <p className="text-xl text-base-content/70 mb-4">No discussions found</p>
                <button onClick={handleNewDiscussion} className="btn btn-primary">
                  Start the First Discussion
                </button>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="btn btn-outline"
                >
                  Previous
                </button>
                <span className="btn btn-ghost">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.pages}
                  className="btn btn-outline"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Discussions;
